import { clearUser } from "@/auth/user.tsx"
import { Err, Ok, Result } from "@/utils"

interface GetOptions {
  method?: "GET"
  use_cache?: boolean
  params?: Record<string, string | number | boolean | null | undefined>
}

interface DeleteOptions {
  method: "DELETE"
  use_cache?: false
}

interface RestDataOptions {
  method?: "POST" | "PUT" | "PATCH"
  body: object
  use_cache?: boolean
}

export type RestOptions = GetOptions | RestDataOptions | DeleteOptions

export interface HttpResponse<T> {
  data: T
  status: number
  headers: Headers
}

export class HttpError extends Error {
  status: number | null

  constructor(message: string, status: number | null) {
    super(message)
    this.status = status
  }
}

const REQUEST_CACHE: Record<string, [Result<HttpResponse<any>, HttpError>, number]> = {}

/** Clear cached GET responses whose cache key includes `pathSubstring` (e.g. `/org-options`). */
export function invalidateApiCache(pathSubstring: string): void {
  for (const key of Object.keys(REQUEST_CACHE)) {
    if (key.includes(pathSubstring)) {
      delete REQUEST_CACHE[key]
    }
  }
}

export function invalidateOrgOptionsCache(fieldName?: string): void {
  invalidateApiCache("/org-options")
  if (fieldName) {
    invalidateApiCache(`field_name=${fieldName}`)
  }
}

const PUBLIC_AUTH_PATHS = new Set([
  "/",
  "/login",
  "/signup",
  "/forgot-password",
  "/reset-password",
  "/select-org",
  "/logout",
])

export function is_session_auth_error(status: number, message: string | undefined): boolean {
  return status === 403 && message === "No user found."
}

/**
 * Whether a failed request says something about our code. A request that never reached the server
 * (`status === null`: dropped connection, page navigated away) and an expired session (already sent
 * to /login) do not, and reporting them buried real errors under "Failed to fetch data".
 */
export function is_reportable_fetch_error(error: HttpError): boolean {
  if (error.status === null) return false
  return !is_session_auth_error(error.status, error.message)
}

/** Pages reachable without a MarginWiz account, matched by prefix because they carry an id. */
const PUBLIC_AUTH_PREFIXES = ["/connect-mailbox/", "/invite/", "/blog/"]

export function should_redirect_to_login(pathname: string): boolean {
  if (PUBLIC_AUTH_PATHS.has(pathname)) {
    return false
  }
  // Bouncing an IT admin who is connecting a mailbox to our login page would strand them: they
  // have no account to log into.
  return !PUBLIC_AUTH_PREFIXES.some(prefix => pathname.startsWith(prefix))
}

export function redirect_to_login(): void {
  const next = window.location.pathname + window.location.search
  window.location.href = "/login?" + new URLSearchParams({ next }).toString()
}

function handle_session_auth_failure(status: number, message: string | undefined): void {
  if (!is_session_auth_error(status, message)) {
    return
  }
  if (!should_redirect_to_login(window.location.pathname)) {
    return
  }
  clearUser()
  redirect_to_login()
}

export function data_or_throw<T>(result: Result<HttpResponse<T>, HttpError>): T {
  if (!result.ok) {
    throw result.error
  }
  return result.value.data
}

export async function api_fetch_throws<T>(path: string, opt?: RestOptions): Promise<T> {
  return data_or_throw(await api_fetch(path, opt))
}

export async function api_fetch<T>(
  path: string,
  opt?: RestOptions
): Promise<Result<HttpResponse<T>, HttpError>> {
  let options = opt ?? {}
  let res
  if ("params" in options) {
    let qs = new URLSearchParams()
    for (let [key, value] of Object.entries(options.params!)) {
      if (value == undefined) {
        continue
      }
      qs.set(key, value.toString())
    }
    path += "?" + qs.toString()
  }
  let cacheKey: string = JSON.stringify([path, options])
  if (!!options.use_cache) {
    let cached = REQUEST_CACHE[cacheKey]
    if (cached) {
      let [result, timestamp] = cached
      if (Date.now() - timestamp < 1000 * 60 * 5) {
        return result
      }
    }
  }
  try {
    res = await fetch("/api" + path, {
      method: options.method ?? ("body" in options ? "POST" : "GET"),
      headers: {
        "Content-Type": "application/json",
        "Accept": "application/json",
      },
      credentials: "same-origin",
      body: "body" in options ? JSON.stringify(options.body) : null,
    })
  } catch (e) {
    // the request timed out or we couldn't connect to the server at all (e.g. server is down).
    return Err(
      new HttpError(
        "There is a problem with your internet connection. Check your connection and try again.",
        null
      )
    )
  }
  const content = await res.text()
  let message
  let data
  try {
    data = JSON.parse(content)
    message = data.message
  } catch (e) {}
  // add to cache
  if (res.status >= 500) {
    message = message ?? "There was a server error. Please try again later."
    return Err(new HttpError(message, res.status))
  } else if (res.status >= 400) {
    message = message ?? "There was a client error."
    handle_session_auth_failure(res.status, message)
    return Err(new HttpError(message, res.status))
  } else {
    let result = Ok({
      data,
      status: res.status,
      headers: res.headers,
    })
    if (!!options.use_cache) {
      REQUEST_CACHE[cacheKey] = [result, Date.now()]
    }
    return result
  }
}

if (import.meta.vitest) {
  const mockFetch = vi.fn()
  global.fetch = mockFetch

  describe("is_reportable_fetch_error", () => {
    it("skips a request that never reached the server", () => {
      expect(is_reportable_fetch_error(new HttpError("connection", null))).toBe(false)
    })
    it("skips an expired session", () => {
      expect(is_reportable_fetch_error(new HttpError("No user found.", 403))).toBe(false)
    })
    it("reports a server error and any other client error", () => {
      expect(is_reportable_fetch_error(new HttpError("boom", 500))).toBe(true)
      expect(is_reportable_fetch_error(new HttpError("bad filter", 400))).toBe(true)
    })
  })

  describe("api_fetch", () => {
    beforeEach(() => {
      vi.resetAllMocks()
    })

    afterEach(() => {
      vi.restoreAllMocks()
    })

    it("should make a successful GET request", async () => {
      const mockResponse = { data: "test data" }
      mockFetch.mockResolvedValueOnce({
        ok: true,
        status: 200,
        text: () => Promise.resolve(JSON.stringify(mockResponse)),
        headers: new Headers(),
      })

      const result = await api_fetch("/test")
      expect(result.ok).toBe(true)
      if (result.ok) {
        expect(result.value.data).toEqual(mockResponse)
        expect(result.value.status).toBe(200)
      }
    })

    it("should make a successful POST request", async () => {
      const mockResponse = { data: "posted data" }
      mockFetch.mockResolvedValueOnce({
        ok: true,
        status: 201,
        text: () => Promise.resolve(JSON.stringify(mockResponse)),
        headers: new Headers(),
      })

      const result = await api_fetch("/test", { method: "POST", body: { test: "data" } })
      expect(result.ok).toBe(true)
      if (result.ok) {
        expect(result.value.data).toEqual(mockResponse)
        expect(result.value.status).toBe(201)
      }
      expect(mockFetch).toHaveBeenCalledWith(
        "/api/test",
        expect.objectContaining({
          method: "POST",
          body: JSON.stringify({ test: "data" }),
        })
      )
    })

    it("should handle network errors", async () => {
      mockFetch.mockRejectedValueOnce(new Error("Network error"))

      const result = await api_fetch("/test")
      expect(result.ok).toBe(false)
      if (!result.ok) {
        expect(result.error).toBeInstanceOf(HttpError)
        expect(result.error.message).toBe(
          "There is a problem with your internet connection. Check your connection and try again."
        )
      }
    })

    it("should handle server errors (5xx)", async () => {
      mockFetch.mockResolvedValueOnce({
        ok: false,
        status: 500,
        text: () => Promise.resolve(JSON.stringify({ message: "Server error" })),
      })

      const result = await api_fetch("/test")
      expect(result.ok).toBe(false)
      if (!result.ok) {
        expect(result.error).toBeInstanceOf(HttpError)
        expect(result.error.message).toBe("Server error")
        expect(result.error.status).toBe(500)
      }
    })

    it("should handle client errors (4xx)", async () => {
      mockFetch.mockResolvedValueOnce({
        ok: false,
        status: 400,
        text: () => Promise.resolve(JSON.stringify({ message: "Bad request" })),
      })

      const result = await api_fetch("/test")
      expect(result.ok).toBe(false)
      if (!result.ok) {
        expect(result.error).toBeInstanceOf(HttpError)
        expect(result.error.message).toBe("Bad request")
        expect(result.error.status).toBe(400)
      }
    })

    it("invalidateApiCache clears matching entries", async () => {
      const mockResponse = { data: "cached data" }
      mockFetch.mockResolvedValue({
        ok: true,
        status: 200,
        text: () => Promise.resolve(JSON.stringify(mockResponse)),
        headers: new Headers(),
      })

      await api_fetch("/org-options", { use_cache: true, params: { field_name: "retailer_name" } })
      invalidateApiCache("/org-options")
      await api_fetch("/org-options", { use_cache: true, params: { field_name: "retailer_name" } })

      expect(mockFetch).toHaveBeenCalledTimes(2)
    })

    it("should use cache when specified", async () => {
      const mockResponse = { data: "cached data" }
      mockFetch.mockResolvedValueOnce({
        ok: true,
        status: 200,
        text: () => Promise.resolve(JSON.stringify(mockResponse)),
        headers: new Headers(),
      })

      // First call to populate cache
      await api_fetch("/test", { use_cache: true })

      // Second call should use cache
      const result = await api_fetch("/test", { use_cache: true })
      expect(result.ok).toBe(true)
      if (result.ok) {
        expect(result.value.data).toEqual(mockResponse)
      }
      expect(mockFetch).toHaveBeenCalledTimes(1) // Fetch should only be called once
    })

    it("should handle query parameters", async () => {
      mockFetch.mockResolvedValueOnce({
        ok: true,
        status: 200,
        text: () => Promise.resolve("{}"),
        headers: new Headers(),
      })

      await api_fetch("/test", { params: { foo: "bar", baz: 123 } })
      expect(mockFetch).toHaveBeenCalledWith("/api/test?foo=bar&baz=123", expect.anything())
    })

    it("redirects to login on session auth failure (403 No user found)", async () => {
      const hrefSetter = vi.fn()
      const location = { pathname: "/deductions", search: "?foo=bar", assign: vi.fn() }
      Object.defineProperty(location, "href", {
        set: hrefSetter,
        get: () => "http://localhost/deductions?foo=bar",
        configurable: true,
      })
      Object.defineProperty(window, "location", { value: location, writable: true, configurable: true })
      mockFetch.mockResolvedValueOnce({
        ok: false,
        status: 403,
        text: () => Promise.resolve(JSON.stringify({ message: "No user found." })),
      })

      await api_fetch("/test")

      expect(hrefSetter).toHaveBeenCalledWith("/login?next=%2Fdeductions%3Ffoo%3Dbar")
    })

    it("does not redirect on Access denied 403", async () => {
      const hrefSetter = vi.fn()
      const location = { pathname: "/deductions", search: "", assign: vi.fn() }
      Object.defineProperty(location, "href", {
        set: hrefSetter,
        get: () => "http://localhost/deductions",
        configurable: true,
      })
      Object.defineProperty(window, "location", { value: location, writable: true, configurable: true })
      mockFetch.mockResolvedValueOnce({
        ok: false,
        status: 403,
        text: () => Promise.resolve(JSON.stringify({ message: "Access denied" })),
      })

      const result = await api_fetch("/test")

      expect(hrefSetter).not.toHaveBeenCalled()
      expect(result.ok).toBe(false)
      if (!result.ok) {
        expect(result.error.message).toBe("Access denied")
      }
    })
  })

  describe("is_session_auth_error", () => {
    it("matches 403 No user found", () => {
      expect(is_session_auth_error(403, "No user found.")).toBe(true)
    })

    it("rejects other 403 messages", () => {
      expect(is_session_auth_error(403, "Access denied")).toBe(false)
    })
  })
}
